import { Box, TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { FormFieldContainer } from '../../../../presentation/components/form-field-container';
import { NewConvergeInputLabel } from '../../../../presentation/components/new-converge-input-label';
import { checkPositiveNumber } from '@converge/utils';

// Custom event type with target.value
type EventWithValue = {
  target: {
    value: string | number;
  };
};

export interface IClaimFormNumberInputAdornmentFieldProps {
  name: string;
  label: string;
  adornmentText: string;
  placeholder?: string;
  paddingLeft?: string;
  onBlur?: (e: EventWithValue) => void;
  disabled?: boolean;
}

export const ClaimFormNumberInputAdornmentField: FC<
  IClaimFormNumberInputAdornmentFieldProps
> = ({
  adornmentText,
  placeholder,
  paddingLeft,
  onBlur,
  name,
  label,
  disabled,
}) => {
  const { control, getValues } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, onBlur: fieldOnBlur, value },
        fieldState: { error },
      }) => (
        <FormFieldContainer md={6}>
          <NewConvergeInputLabel
            label={label}
            className={error?.message ? 'error-message' : ''}
          />

          <NumericFormat
            name={name}
            onBlur={() => {
              if (fieldOnBlur) {
                fieldOnBlur();
              }

              if (onBlur) {
                const currentValue = getValues(name);
                const syntheticEvent: EventWithValue = {
                  target: { value: currentValue },
                };
                onBlur(syntheticEvent);
              }
            }}
            customInput={TextField}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontWeight: 600,
                fontSize: '14px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #23a892',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #23a892',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid #4d4d4d',
                },
                '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid #bfbdbd',
                },
                '& input::placeholder': {
                  opacity: 1,
                  fontWeight: 600,
                  color: 'rgba(0, 0, 0, 0.6) ',
                },
              },
            }}
            variant="outlined"
            placeholder={placeholder}
            fullWidth
            error={!!error}
            size="small"
            value={value || ''}
            onValueChange={(v) => {
              // Store formatted value to show commas during typing
              onChange(v.formattedValue);
            }}
            className="form-number-background"
            thousandSeparator=","
            isAllowed={(values) => {
              // First check if it's a positive number
              if (!checkPositiveNumber(values)) {
                return false;
              }

              // Then check 32-bit signed integer limit (2,147,483,647)
              const MAX_INT_VALUE = 2147483647;
              if (values.floatValue && values.floatValue > MAX_INT_VALUE) {
                return false;
              }

              return true;
            }}
            helperText={
              error ? (
                <Box
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {error.message as string}
                </Box>
              ) : (
                ''
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: disabled ? 'rgba(0, 0, 0, 0.4)' : 'black',
                      paddingLeft: paddingLeft ?? '',
                    }}
                  >
                    <Box>{adornmentText}</Box>
                  </Box>
                </InputAdornment>
              ),
            }}
            disabled={disabled}
          />
        </FormFieldContainer>
      )}
    />
  );
};
